import { createContext, useState, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { reservationService, type Reservation, type CreateReservationData } from '../../../shared/services/reservation/reservationService';

interface ReservationContextData {
  reservations: Reservation[];
  isLoading: boolean;
  loading: boolean;
  error: string | null;
  handleCreate: () => void;
  handleView: (reservation: Reservation) => void;
  handleCloseError: () => void;
  
  isDeleteModalOpen: boolean;
  selectedReservation: Reservation | null;
  openDeleteModal: (reservation: Reservation) => void;
  closeDeleteModal: () => void;
  handleDeleteReservation: () => void;
  isDeleting: boolean;
  
  navigateToEdit: (id: string) => void;

  reservationData: Reservation | null;
  isDataLoading: boolean;
  createReservation: (data: CreateReservationData) => Promise<void>;
  updateReservation: (id: string, data: Partial<CreateReservationData>) => Promise<void>;
  isSubmitting: boolean;
  getReservationById: (id: string) => Promise<Reservation>;
  setError: (error: string | null) => void;
  
  checkAvailability: (date: string, roomId: string) => Promise<any>;
  getReservationsByTenant: (tenantId: string) => Promise<Reservation[]>;
}

interface ReservationProviderProps {
  children: ReactNode;
}

export const ReservationContext = createContext<ReservationContextData>({} as ReservationContextData);

export function ReservationProvider({ children }: ReservationProviderProps) {
  const navigate = useNavigate();
  const { id } = useParams();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const { data: reservations = [], isLoading } = useQuery({
    queryKey: ['reservations'],
    queryFn: async () => {
      try {
        const response = await reservationService.list();
        return response || [];
      } catch (error: any) {
        const errorMessage = error.response?.data?.message || error.message || 'Erro ao buscar reservas';
        setError(errorMessage);
        return [];
      }
    }
  });

  const { data: reservationData = null, isLoading: isDataLoading } = useQuery({
    queryKey: ['reservation', id],
    queryFn: async () => {
      if (!id) return null;
      try {
        return await reservationService.getById(id);
      } catch (error: any) {
        const errorMessage = error.response?.data?.message || error.message || 'Erro ao buscar reserva';
        setError(errorMessage);
        return null;
      }
    },
    enabled: !!id,
  });

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedReservation, setSelectedReservation] = useState<Reservation | null>(null);

  const deleteMutation = useMutation({
    mutationFn: (id: string) => reservationService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reservations'] });
      closeDeleteModal();
      // Verificar se estamos na página de detalhes e navegar para lista
      if (window.location.pathname.includes('/reservations/') && 
          !window.location.pathname.includes('/new') && 
          !window.location.pathname.includes('/edit')) {
        navigate('/reservations');
      }
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.message || error.message || 'Erro ao deletar reserva';
      setError(errorMessage);
    }
  });

  const createMutation = useMutation({
    mutationFn: (data: CreateReservationData) => reservationService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reservations'] });
      navigate('/reservations');
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.message || error.message || 'Erro ao criar reserva';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<CreateReservationData> }) => 
      reservationService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reservations'] });
      queryClient.invalidateQueries({ queryKey: ['reservation', id] });
      navigate('/reservations');
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.message || error.message || 'Erro ao atualizar reserva';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  });

  const handleCreate = () => navigate('/reservations/new');
  const handleView = (reservation: Reservation) => navigate(`/reservations/${reservation.id}`);
  const handleCloseError = () => setError(null);

  const openDeleteModal = (reservation: Reservation) => {
    setSelectedReservation(reservation);
    setIsDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setSelectedReservation(null);
  };

  const handleDeleteReservation = () => {
    if (selectedReservation?.id) {
      deleteMutation.mutate(selectedReservation.id);
    }
  };

  const navigateToEdit = (id: string) => navigate(`/reservations/${id}/edit`);

  const createReservation = async (data: CreateReservationData) => {
    await createMutation.mutateAsync(data);
  };

  const updateReservation = async (id: string, data: Partial<CreateReservationData>) => {
    await updateMutation.mutateAsync({ id, data });
  };

  const getReservationById = async (id: string): Promise<Reservation> => {
    return await reservationService.getById(id);
  };

  const checkAvailability = async (date: string, roomId: string) => {
    return await reservationService.checkAvailability(date, roomId);
  };

  const getReservationsByTenant = async (tenantId: string): Promise<Reservation[]> => {
    return await reservationService.getByTenant(tenantId);
  };

  return (
    <ReservationContext.Provider
      value={{
        reservations,
        isLoading,
        loading: createMutation.isPending || updateMutation.isPending,
        error,
        handleCreate,
        handleView,
        handleCloseError,
        
        isDeleteModalOpen,
        selectedReservation,
        openDeleteModal,
        closeDeleteModal,
        handleDeleteReservation,
        isDeleting: deleteMutation.isPending,
        
        navigateToEdit,

        reservationData,
        isDataLoading,
        createReservation,
        updateReservation,
        isSubmitting: createMutation.isPending || updateMutation.isPending,
        getReservationById,
        setError,
        
        checkAvailability,
        getReservationsByTenant,
      }}
    >
      {children}
    </ReservationContext.Provider>
  );
}